import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import { useAuth } from '../../hooks/useAuth';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SubmitHero from './components/SubmitHero';
import StepIndicator from './components/StepIndicator';
import AccountStep from './components/AccountStep';
import ProfileStep from './components/ProfileStep';
import PlanStep from './components/PlanStep';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

export interface BusinessFormData {
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  website: string;
  category: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  yearEstablished: string;
  numberOfEmployees: string;
  services: string;
  description: string;
  tagline: string;
  specialNotes: string;
  photos: Array<{ id: string; url: string; name: string; type: string }>;
  hours: Array<{ day: string; open: string; close: string; closed: boolean }>;
  facebook: string;
  instagram: string;
  linkedin: string;
}

const defaultFormData: BusinessFormData = {
  businessName: '',
  ownerName: '',
  email: '',
  phone: '',
  website: '',
  category: '',
  address: '',
  city: 'Roseville',
  state: 'CA',
  zip: '',
  yearEstablished: '',
  numberOfEmployees: '',
  services: '',
  description: '',
  tagline: '',
  specialNotes: '',
  photos: [],
  hours: [
    { day: 'Monday', open: '09:00', close: '17:00', closed: false },
    { day: 'Tuesday', open: '09:00', close: '17:00', closed: false },
    { day: 'Wednesday', open: '09:00', close: '17:00', closed: false },
    { day: 'Thursday', open: '09:00', close: '17:00', closed: false },
    { day: 'Friday', open: '09:00', close: '17:00', closed: false },
    { day: 'Saturday', open: '10:00', close: '15:00', closed: false },
    { day: 'Sunday', open: '10:00', close: '15:00', closed: true },
  ],
  facebook: '',
  instagram: '',
  linkedin: '',
};

export default function SubmitBusinessPage() {
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [formData, setFormData] = useState<BusinessFormData>(defaultFormData);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useSEO({
    title: 'Submit Your Business | Roseville CA Business Directory | Roseville.org',
    description:
      'Add your business to the Roseville.org directory. Choose from Basic, Featured, or Premium listings. Reach thousands of local customers in Roseville, California.',
    keywords:
      'submit business Roseville, add business listing, Roseville directory listing, local business Roseville CA, Placer County business directory',
    canonical: '/submit-business',
  });

  // Handle Stripe redirect back
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const plan = searchParams.get('plan');
    const sessionId = searchParams.get('session_id');

    if (success === 'true' && plan && sessionId) {
      setSubmitSuccess(true);
    } else if (canceled === 'true') {
      setCurrentStep(3);
      setError('Payment was canceled. Please try again or select a different plan.');
    }
  }, [searchParams]);

  // Auto-advance to step 2 if already logged in
  useEffect(() => {
    if (isAuthenticated && currentStep === 1) {
      setCurrentStep(2);
      if (user?.email) {
        setFormData(prev => ({
          ...prev,
          email: user.email || '',
          ownerName: user.user_metadata?.full_name || '',
        }));
      }
    }
  }, [isAuthenticated, user]);

  const handleAccountCreated = () => {
    setCurrentStep(2);
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        ownerName: user.user_metadata?.full_name || '',
      }));
    }
  };

  const handleProfileComplete = (data: BusinessFormData) => {
    setFormData(data);
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectPlan = async (planName: string) => {
    setIsProcessing(true);
    setError('');

    try {
      // Submit business profile data first
      const formElement = document.createElement('form');
      formElement.action = 'https://readdy.ai/api/form/d6bra32ohb161tfd7ok0';
      formElement.method = 'POST';
      formElement.setAttribute('data-readdy-form', '');
      formElement.style.display = 'none';

      const fields: Record<string, string> = {
        'Business Name': formData.businessName,
        'Owner Name': formData.ownerName,
        email: formData.email,
        'Phone': formData.phone,
        'Website': formData.website || 'Not provided',
        'Category': formData.category,
        'Address': `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
        'Year Established': formData.yearEstablished || 'Not provided',
        'Number of Employees': formData.numberOfEmployees || 'Not provided',
        'Services': formData.services || 'Not provided',
        'Tagline': formData.tagline || 'Not provided',
        'Description': formData.description,
        'Special Notes': formData.specialNotes || 'Not provided',
        'Operating Hours': formData.hours
          .map(h => `${h.day}: ${h.closed ? 'Closed' : `${h.open} - ${h.close}`}`)
          .join('; '),
        'Facebook': formData.facebook || 'Not provided',
        'Instagram': formData.instagram || 'Not provided',
        'LinkedIn': formData.linkedin || 'Not provided',
        'Selected Plan': planName,
        'Number of Photos': formData.photos.length.toString(),
        'Submission Date': new Date().toLocaleString(),
        'Status': planName === 'Free Listing' ? 'Pending Review' : 'Pending Payment',
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        formElement.appendChild(input);
      });

      document.body.appendChild(formElement);
      const fd = new FormData(formElement);
      await fetch(formElement.action, {
        method: 'POST',
        body: new URLSearchParams(fd as any),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      document.body.removeChild(formElement);

      // If Free Listing, skip payment and show success
      if (planName === 'Free Listing') {
        setSubmitSuccess(true);
        setIsProcessing(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // For paid plans, create Stripe checkout
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/create-business-listing-checkout`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            planName,
            email: formData.email,
            businessName: formData.businessName,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to process. Please try again.'
      );
      setIsProcessing(false);
    }
  };

  if (submitSuccess) {
    const planName = searchParams.get('plan') || 'Business Listing';
    return (
      <div className="min-h-screen bg-gray-50/50">
        <Navbar />
        <SubmitHero />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Listing Submitted Successfully!
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Your payment has been processed and your business listing is now under review.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8 text-left">
              <p className="text-gray-700 mb-2">
                <strong>What happens next?</strong>
              </p>
              <p className="text-gray-600 text-sm">
                Our team will review your business listing within{' '}
                <strong>48 hours</strong>. Once approved, your listing will be
                published on the Roseville Business Directory. You&apos;ll receive an
                email confirmation when your listing goes live.
              </p>
            </div>
            <div className="space-y-2 mb-8">
              <p className="text-gray-600">
                <strong>Selected Plan:</strong> {planName}
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <a
                href="/directory"
                className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Directory
              </a>
              <a
                href="/owner-dashboard"
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Owner Dashboard
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar />
      <SubmitHero />

      <StepIndicator currentStep={currentStep} />

      {error && (
        <div className="max-w-4xl mx-auto px-6 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-red-900 mb-1">Error</h4>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-auto w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <AccountStep onSuccess={handleAccountCreated} />
      )}

      {currentStep === 2 && (
        <ProfileStep
          formData={formData}
          setFormData={setFormData}
          onComplete={handleProfileComplete}
          onBack={() => {}}
        />
      )}

      {currentStep === 3 && (
        <PlanStep
          onSelectPlan={handleSelectPlan}
          isProcessing={isProcessing}
          onBack={() => setCurrentStep(2)}
        />
      )}

      <Footer />
    </div>
  );
}
